import type { Account, Member, ReportCategoryStat, StatisticsReport } from "../../lib/types";
import type { MonthlyAccountFilter } from "./MonthlyExpenseBreakdown";
import { categoryPalette } from "./StatisticsRankings";
import { buildAllTransactionsBreakdown } from "./statisticsModels";

function accountOwnerLabel(accountId: string, accounts: Account[], members: Member[]) {
  const account = accounts.find((item) => item.id === accountId);
  if (!account?.owner_user_id) return "Shared";
  const owner = members.find((member) => member.user_id === account.owner_user_id);
  return owner?.display_name || owner?.email || "Unknown user";
}

export function buildMonthlyBreakdownModel({
  report,
  accountReport,
  accountOptionsReport,
  reportCategoryItems,
  accounts,
  members,
  overviewType,
  selectedLeafCategoryId,
  selectedAccountId,
  fallbackCurrency,
}: {
  report?: StatisticsReport;
  accountReport?: StatisticsReport;
  accountOptionsReport?: StatisticsReport;
  reportCategoryItems: ReportCategoryStat[];
  accounts: Account[];
  members: Member[];
  overviewType: string;
  selectedLeafCategoryId: string;
  selectedAccountId: string;
  fallbackCurrency: string;
}) {
  const showAccountBreakdown = overviewType === "transfer" || Boolean(selectedLeafCategoryId);
  const accountBreakdown: ReportCategoryStat[] = [];
  if (accountReport && showAccountBreakdown) {
    accountReport.accounts.forEach((account, index) => {
      const amount = Number(
        overviewType === "income"
          ? account.base_income
          : overviewType === "expense"
            ? account.base_expense
            : account.base_transfer_out,
      );
      if (amount <= 0) return;
      const accountDetails = accounts.find((item) => item.id === account.account_id);
      accountBreakdown.push({
        category_id: account.account_id,
        category_name: `${account.account_name} · ${accountOwnerLabel(account.account_id, accounts, members)}`,
        icon: accountDetails?.icon || (overviewType === "income" ? "banknote-in" : overviewType === "expense" ? "banknote-out" : "wallet"),
        color: accountDetails?.color || categoryPalette[index % categoryPalette.length],
        amount: String(amount),
        count: 0,
        average_amount: String(amount),
        percent: 0,
        previous_amount: "0",
        amount_delta: String(amount),
      });
    });
  }
  const accountTotal = accountBreakdown.reduce((sum, item) => sum + Number(item.amount), 0);
  accountBreakdown.forEach((item) => {
    item.percent = accountTotal ? Number(item.amount) / accountTotal * 100 : 0;
  });

  const accountFilters: MonthlyAccountFilter[] = [];
  if (showAccountBreakdown) {
    for (const account of accountOptionsReport?.accounts || []) {
      const amount = Number(
        overviewType === "income"
          ? account.income
          : overviewType === "expense"
            ? account.expense
            : account.transfer_out,
      );
      if (amount <= 0) continue;
      const accountDetails = accounts.find((item) => item.id === account.account_id);
      accountFilters.push({
        id: account.account_id,
        name: `${account.account_name} · ${accountOwnerLabel(account.account_id, accounts, members)}`,
        currency: account.currency,
        amount,
        icon: accountDetails?.icon || (overviewType === "income" ? "banknote-in" : overviewType === "expense" ? "banknote-out" : "wallet"),
        color: accountDetails?.color,
        selected: selectedAccountId === account.account_id,
      });
    }
  }

  const allTransactionsBreakdown = buildAllTransactionsBreakdown(report);
  const transferTotal = Number(allTransactionsBreakdown.find((item) => item.category_id === "transfer")?.amount || 0);
  const chartItems = !overviewType
    ? allTransactionsBreakdown
    : showAccountBreakdown
      ? accountBreakdown
      : reportCategoryItems;
  const chartTotal = overviewType === "income"
    ? selectedLeafCategoryId ? String(accountTotal) : report?.totals.income || "0"
    : overviewType === "transfer"
      ? String(accountTotal)
      : overviewType === "expense"
        ? selectedLeafCategoryId ? String(accountTotal) : report?.totals.expense || "0"
        : String(Number(report?.totals.expense || 0) + Number(report?.totals.income || 0) + transferTotal);

  return {
    showAccountBreakdown,
    accountBreakdown,
    accountFilters,
    chartItems,
    chartTotal,
    chartCurrency: showAccountBreakdown ? accountReport?.base_currency || fallbackCurrency : fallbackCurrency,
    chartLabel: overviewType === "income" ? "Income" : overviewType === "transfer" ? "Transfers" : overviewType === "expense" ? "Expenses" : "Transactions",
  };
}

export function statisticsAccountOwnerLabel(accountId: string, accounts: Account[], members: Member[]) {
  return accountOwnerLabel(accountId, accounts, members);
}
